<template>
  <validation-observer ref="simpleRules">
    <b-form v-if="item">
      <p><strong class="text-primary">{{ $t('General Settings') }}</strong></p>
      <b-row>
        <!-- Default Digit Count -->
        <b-col>
          <b-form-group
            :label="$t('Default Digit Count')"
            label-for="defaultDigitCount"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Default Digit Count')"
              rules="required|between:0,5"
            >
              <b-form-input
                v-model="item.defaultDigitCount"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('Default Digit Count (0-5)')"
              />
              <small class="text-primary">{{ $t(errors[0]) }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <!-- Default Minimum Base -->
        <b-col>
          <b-form-group
            :label="$t('Default Minimum Base')"
            label-for="defaultMinimumBase"
          >
            <validation-provider
              #default="{ errors }"
              :name="$t('Default Minimum Base')"
              rules="required|between:0,30"
            >
              <b-form-input
                v-model="item.defaultMinimumBase"
                :state="errors.length > 0 ? false : null"
                :placeholder="$t('Default Minimum Base (0-30)')"
              />
              <small class="text-primary">{{ $t(errors[0]) }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>

      </b-row>

      <!-- Visible Survey Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Visible Survey Questions')"
            label-for="mc-visibleSurveyQuestionNames"
          >
            <QuestionOrder
              :id="'visibleSurveyQuestionNames'"
              v-model="visibleSurveyQuestionNames"
              :options="optionQuestion"
              @input="item.visibleSurveyQuestionNames = visibleSurveyQuestionNames"
            />
            <v-select
              id="vue-select"
              v-model="visibleSurveyQuestionNames"
              :options="optionQuestion"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.visibleSurveyQuestionNames = visibleSurveyQuestionNames"
            />

          </b-form-group>
        </b-col>
      </b-row>

      <!-- Filter Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Filter Questions')"
            label-for="mc-filterQuestionNames"
          >
            <QuestionOrder
              :id="'filterQuestionNames'"
              v-model="filterQuestionNames"
              :options="optionQuestion"
              @input="item.filterQuestionNames = filterQuestionNames"
            />
            <v-select
              id="vue-select-filter-questions"
              v-model="filterQuestionNames"
              :options="optionQuestion"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.filterQuestionNames = filterQuestionNames"
            />

          </b-form-group>

        </b-col>
      </b-row>

      <b-row>
        <!-- Permission questions -->
        <b-col>
          <b-form-group
            :label="$t('Permission Questions')"
            label-for="mc-permissionQuestionNames"
          >
            <QuestionOrder
              :id="'permissionQuestionNames'"
              v-model="permissionQuestionNames"
              :options="optionQuestion"
              @input="item.permissionQuestionNames = permissionQuestionNames"
            />
            <v-select
              id="vue-select"
              v-model="permissionQuestionNames"
              :options="optionQuestion"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.permissionQuestionNames = permissionQuestionNames"
            />
          </b-form-group>
        </b-col>

        <!-- Default Weight Column -->
        <b-col>
          <b-form-group
            :label="$t('Default Weight Column (Numeric)')"
            label-for="mc-defaultWeightColumn"
          >
            <v-select
              id="vue-select"
              v-model="defaultWeightColumn"
              :options="optionQuestionNumeric"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select default weight column...')"
              @input="item.defaultWeightColumn = defaultWeightColumn"
            />
          </b-form-group>

        </b-col>

        <!-- Default Weight Column Message-->
        <b-col v-if="item.defaultWeightColumn">
          <b-form-group
            :label="$t('Default Weight Column Message')"
            label-for="mc-defaultWeightColumnMessage"
          >
            <b-form-input
              v-model="item.defaultWeightColumnMessage"
              :placeholder="$t('Default Weight Column Message...')"
            />
          </b-form-group>

        </b-col>

      </b-row>

      <b-row>
        <b-col md="2">
          <!-- Multimedia -->
          <b-form-group
            :label="$t('Multimedia Active')"
            label-for="mc-multimedia"
          >
            <b-form-checkbox
              id="checkbox-multimedia"
              v-model="item.multimedia"
              name="checkbox-multimedia"
              switch
            >
              <span v-if="item.multimedia">{{ $t('Yes') }}</span>
              <span v-if="!item.multimedia">{{ $t('No') }}</span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col class="p-2">

          <!-- Hidden file input -->
          <input
            id="hidden-file-input"
            ref="fileInput"
            type="file"
            style="display: none;"
            accept=".xlsx"
            @change="handleFileUpload"
          >

          <!-- Upload Media File -->
          <b-link @click="triggerFileInput">
            Update Media Files
          </b-link>
        </b-col>
      </b-row>

      <!-- Hot Alert Config -->
      <p class="mt-2">
        <strong class="text-primary">{{ $t('HotAlert Config') }}</strong>
      </p>

      <b-row>
        <!-- Hot Alert Action Options -->
        <b-col>
          <b-form-group
            :label="$t('Hot Alert Action Options')"
            label-for="mc-hotAlertActionOptions"
          >
            <b-form-tags
              v-model="item.hotAlertActionOptions"
              input-id="tags-hotAlertActionOptions"
              class="mb-2"
            />
          </b-form-group>
        </b-col>
        <!-- Hot Alert Taken Action Options -->
        <b-col>
          <b-form-group
            :label="$t('Hot Alert Taken Action Options')"
            label-for="mc-hotAlertTakenActionOptions"
          >
            <b-form-tags
              v-model="item.hotAlertTakenActionOptions"
              input-id="tags-hotAlertTakenActionOptions"
              class="mb-2"
            />
          </b-form-group>
        </b-col>
        <!-- Hot Alert Categories -->
        <b-col>
          <b-form-group
            :label="$t('Hot Alert Categories')"
            label-for="mc-hotAlertCategories"
          >
            <b-form-tags
              v-model="item.hotAlertCategories"
              input-id="tags-hotAlertCategories"
              class="mb-2"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Hot Alert List Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Hot Alert LIST Questions')"
            label-for="mc-hotAlertListQuestionNames"
          >
            <QuestionOrder
              :id="'hotAlertListQuestionNames'"
              v-model="hotAlertListQuestionNames"
              :options="optionQuestion"
              @input="item.hotAlertListQuestionNames = hotAlertListQuestionNames"
            />
            <v-select
              id="vue-select-list-q"
              v-model="hotAlertListQuestionNames"
              :options="hotalertListQuestionOptions"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.hotAlertListQuestionNames = hotAlertListQuestionNames"
            />

          </b-form-group>

        </b-col>
      </b-row>
      <!-- Hot Alert Service Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Hot Alert SERVICE Questions')"
            label-for="mc-hotAlertServiceQuestionNames"
          >
            <QuestionOrder
              :id="'hotAlertServiceQuestionNames'"
              v-model="hotAlertServiceQuestionNames"
              :options="optionQuestion"
              @input="item.hotAlertServiceQuestionNames = hotAlertServiceQuestionNames"
            />
            <v-select
              id="vue-select"
              v-model="hotAlertServiceQuestionNames"
              :options="hotalertServiceQuestionOptions"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.hotAlertServiceQuestionNames = hotAlertServiceQuestionNames"
            />

          </b-form-group>

        </b-col>
      </b-row>
      <!-- Hot Alert Analysis Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Hot Alert ANALYSIS Questions')"
            label-for="mc-hotAlertAnalysisQuestionNames"
          >
            <QuestionOrder
              :id="'hotAlertAnalysisQuestionNames'"
              v-model="hotAlertAnalysisQuestionNames"
              :options="optionQuestion"
              @input="item.hotAlertAnalysisQuestionNames = hotAlertAnalysisQuestionNames"
            />
            <v-select
              id="vue-select-list-q"
              v-model="hotAlertAnalysisQuestionNames"
              :options="hotalertListQuestionOptions"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.hotAlertAnalysisQuestionNames = hotAlertAnalysisQuestionNames"
            />

          </b-form-group>

        </b-col>
      </b-row>

      <!-- Hot Alert Mail Template -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Hot Alert Mail Template')"
            label-for="mc-hotAlertMailTemplate"
          >
            <b-form-textarea
              v-model="item.hotAlertMailTemplate"
              :placeholder="$t('Hot alert mail template...')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
          <small>
            <b-badge variant="success">[createdate]</b-badge> <b-badge variant="success">[categories]</b-badge> <b-badge
              variant="success"
            >[closedby]</b-badge> <b-badge variant="success">[closedat]</b-badge> <b-badge
              variant="success"
            >[contactstatus]</b-badge> <b-badge variant="success">[categoriesdescription]</b-badge>
          </small>

        </b-col>
      </b-row>

      <!-- Hot Alert Closed Mail Template -->
      <b-row class="mt-2">
        <b-col>
          <b-form-group
            :label="$t('Hot Alert Closed Mail Template')"
            label-for="mc-hotAlertClosedMailTemplate"
          >
            <b-form-textarea
              v-model="item.hotAlertClosedMailTemplate"
              :placeholder="$t('Hot alert closed mail template...')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
          <small>
            <b-badge variant="success">[createdate]</b-badge> <b-badge variant="success">[categories]</b-badge> <b-badge
              variant="success"
            >[closedby]</b-badge> <b-badge variant="success">[closedat]</b-badge> <b-badge
              variant="success"
            >[contactstatus]</b-badge> <b-badge variant="success">[categoriesdescription]</b-badge> <b-badge variant="success">[actiontaken]</b-badge> <b-badge variant="success">[actiontakendescription]</b-badge>
          </small>
        </b-col>
      </b-row>

      <!-- Hotalert Conditions -->
      <p class="mt-2">
        <strong class="text-primary">{{ $t('Hotalert Conditions') }}</strong>
      </p>
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('No hotalert on Saturday')"
            label-for="mc-noHotAlertTransferOnSaturday"
          >
            <b-form-checkbox
              id="checkbox-noHotAlertTransferOnSaturday"
              v-model="item.noHotAlertTransferOnSaturday"
              name="checkbox-noHotAlertTransferOnSaturday"
              switch
            >
              <span v-if="item.noHotAlertTransferOnSaturday">{{ $t('Yes') }}</span>
              <span v-if="!item.noHotAlertTransferOnSaturday">{{ $t('No') }}</span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            :label="$t('No hotalert on Sunday')"
            label-for="mc-noHotAlertTransferOnSunday"
          >
            <b-form-checkbox
              id="checkbox-noHotAlertTransferOnSunday"
              v-model="item.noHotAlertTransferOnSunday"
              name="checkbox-noHotAlertTransferOnSunday"
              switch
            >
              <span v-if="item.noHotAlertTransferOnSunday">{{ $t('Yes') }}</span>
              <span v-if="!item.noHotAlertTransferOnSunday">{{ $t('No') }}</span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            :label="$t('No hotalert on Holidays')"
            label-for="mc-noHotAlertTransferOnHolidays"
          >
            <b-form-checkbox
              id="checkbox-noHotAlertTransferOnHolidays"
              v-model="item.noHotAlertTransferOnHolidays"
              name="checkbox-noHotAlertTransferOnHolidays"
              switch
            >
              <span v-if="item.noHotAlertTransferOnHolidays">{{ $t('Yes') }}</span>
              <span v-if="!item.noHotAlertTransferOnHolidays">{{ $t('No') }}</span>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Approval Config -->
      <p class="mt-2">
        <strong class="text-primary">{{ $t('Approval Config') }}</strong>
      </p>

      <!-- Approval List Questions -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Approval LIST Questions')"
            label-for="mc-approvalListQuestionNames"
          >
            <QuestionOrder
              :id="'approvalListQuestionNames'"
              v-model="approvalListQuestionNames"
              :options="optionQuestion"
              @input="item.approvalListQuestionNames = approvalListQuestionNames"
            />
            <v-select
              id="vue-select-list-q-approvals"
              v-model="approvalListQuestionNames"
              :options="approvalListQuestionOptions"
              :reduce="question => question.questionName"
              label="questionText"
              :placeholder="$t('Select question names...')"
              multiple
              @input="item.approvalListQuestionNames = approvalListQuestionNames"
            />

          </b-form-group>

        </b-col>
      </b-row>

      <!-- Approval Mail Template -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Approval Mail Template')"
            label-for="mc-approvalMailTemplate"
          >
            <b-form-textarea
              v-model="item.approvalMailTemplate"
              :placeholder="$t('Approval mail template...')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Data Responsibles -->
      <p class="mt-2">
        <strong class="text-primary">{{ $t('Data Responsibles') }}</strong>
      </p>

      <!-- Data Responsible Emails -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Data Responsible Emails')"
            label-for="mc-dataResponsibleEmails"
          >
            <b-form-textarea
              v-model="item.dataResponsibleEmails"
              :placeholder="$t('Email addresses separated with `;` (default IpsosLoyaltyMMUretim@ipsos.com)')"
              rows="3"
              max-rows="6"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- GDPR -->
      <p class="mt-2">
        <strong class="text-primary">GDPR</strong>
      </p>

      <!-- GDPR Query Endpoint -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('GDPR Query Endpoint')"
            label-for="mc-gdprQueryEndPoint"
          >
            <b-form-input
              v-model="item.GdprQueryEndPoint"
              placeholder="https://x.x.x.x"
            />
          </b-form-group>

        </b-col>
      </b-row>

      <!-- Appeal Config -->
      <template>
        <p class="mt-2">
          <strong class="text-primary">{{ $t('Appeal Config') }}</strong>
        </p>
        <!-- AppealOnSurvey -->
        <b-row>
          <b-col>
            <b-form-group
              :label="$t('Appeal On Survey')"
              label-for="mc-appealOnSurvey"
            >
              <b-form-checkbox
                id="checkbox-appealOnSurvey"
                v-model="item.appealOnSurvey"
                name="checkbox-appealOnSurvey"
                switch
              >
                <span v-if="item.appealOnSurvey">{{ $t('Yes') }}</span>
                <span v-if="!item.appealOnSurvey">{{ $t('No') }}</span>
              </b-form-checkbox>

            </b-form-group>
          </b-col>
        </b-row>
        <!-- Appeal Questions -->
        <b-row>
          <b-col>
            <b-form-group
              :label="$t('Appeal Questions')"
              label-for="mc-appealQuestionNames"
            >
              <v-select
                id="vue-select-list-q"
                v-model="appealQuestionNames"
                :options="appealQuestionOptions"
                :reduce="question => question.questionName"
                label="questionText"
                :placeholder="$t('Select question names...')"
                multiple
                @input="item.appealQuestionNames = appealQuestionNames"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <!-- Appeal Periods -->
          <b-col>
            <b-form-group
              :label="$t('Appeal Periods')"
              label-for="mc-appealPeriods"
            >
              <v-select
                id="vue-select-list-q"
                v-model="appealPeriods"
                :taggable="true"
                :placeholder="$t('Enter periods...')"
                multiple
                @input="item.appealPeriods = appealPeriods"
              />
            </b-form-group>
          </b-col>
          <!-- Max Appeals Per Period -->
          <b-col>
            <b-form-group
              :label="$t('Max Appeals % Per Period (0 = unlimited)')"
              label-for="mc-maxAppealsPerPeriod"
            >
              <b-input-group append="%">
                <b-form-input
                  id="mc-maxAppealsPerPeriod"
                  v-model="item.maxAppealsPerPeriod"
                  type="number"
                  :placeholder="$t('0')"
                />
              </b-input-group>
            </b-form-group>
          </b-col>
          <!-- Appeal Start Date -->
          <b-col>
            <b-form-group
              :label="$t('Appeal Start Date & Time (Optional)')"
              label-for="mc-appeal-start-date"
            >
              <b-input-group>
                <flat-pickr
                  id="mc-appeal-start-date"
                  ref="appealStartDatePicker"
                  v-model="item.appealStartDate"
                  class="form-control"
                  :config="{ enableTime: true,dateFormat: 'Y-m-d H:i'}"
                  :placeholder="$t('Select date & time...')"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    :disabled="!item.appealStartDate"
                    :title="$t('Clear')"
                    :aria-label="$t('Clear')"
                    @click="clearAppealDate('appealStartDate', 'appealStartDatePicker')"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
          <!-- Appeal End Date -->
          <b-col>
            <b-form-group
              :label="$t('Appeal End Date & Time (Optional)')"
              label-for="mc-appeal-end-date"
            >
              <b-input-group>
                <flat-pickr
                  id="mc-appeal-end-date"
                  ref="appealEndDatePicker"
                  v-model="item.appealEndDate"
                  class="form-control"
                  :config="{ enableTime: true,dateFormat: 'Y-m-d H:i'}"
                  :placeholder="$t('Select date & time...')"
                />
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    :disabled="!item.appealEndDate"
                    :title="$t('Clear')"
                    :aria-label="$t('Clear')"
                    @click="clearAppealDate('appealEndDate', 'appealEndDatePicker')"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <!-- Appeal Reasons -->
        <b-row>
          <b-col>
            <b-form-group
              :label="$t('Appeal Reasons')"
              label-for="mc-appealReasons"
            >
              <v-select
                id="vue-select-list-q"
                v-model="appealReasons"
                :taggable="true"
                :placeholder="$t('Enter reasons...')"
                multiple
                @input="item.appealReasons = appealReasons"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <!-- Appeal Mail Questions -->
        <b-row>
          <b-col>
            <b-form-group
              :label="$t('Appeal Mail Questions')"
              label-for="mc-AppealMailQuestionNames"
            >
              <QuestionOrder
                :id="'appealMailQuestionNames'"
                v-model="appealMailQuestionNames"
                :options="optionQuestion"
                @input="item.appealMailQuestionNames = appealMailQuestionNames"
              />
              <v-select
                id="vue-select"
                v-model="appealMailQuestionNames"
                :options="optionQuestion"
                :reduce="question => question.questionName"
                label="questionText"
                :placeholder="$t('Select question names...')"
                multiple
                @input="item.appealMailQuestionNames = appealMailQuestionNames"
              />

            </b-form-group>
          </b-col>
        </b-row>

      </template>

      <!-- AI -->

      <p class="mt-2">
        <strong class="text-primary">{{ $t('Project Description for AI') }}</strong>
      </p>
      <!-- Project Description for AI -->
      <b-row>
        <b-col>
          <b-form-group
            :label="$t('Description')"
            label-for="mc-projectDescription"
          >
            <b-form-textarea
              v-model="item.projectDescription"
              :placeholder="$t('Description...')"
              rows="3"
              max-rows="6"
              max-length="500"
            />
          </b-form-group>
        </b-col>
      </b-row>

    </b-form>
  </validation-observer>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import QuestionOrder from '@/layouts/components/QuestionOrder.vue'

import {
  BFormGroup, BFormInput, BForm, BRow, BCol, BFormTags, BFormTextarea, BFormCheckbox, BBadge, BLink,
  BInputGroup, BInputGroupAppend, BButton,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'

export default {
  components: {
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BForm,
    BRow,
    BCol,
    BFormTags,
    vSelect,
    BFormCheckbox,
    BBadge,
    QuestionOrder,
    BLink,
    BInputGroup,
    BInputGroupAppend,
    BButton,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      defaultItem: {
        defaultDigitCount: 2,
        defaultMinimumBase: 30,
        visibleSurveyQuestionNames: [],
        appealMailQuestionNames: [],
        filterQuestionNames: [],
        permissionQuestionNames: [],
        defaultWeightColumn: null,
        defaultWeightColumnMessage: null,
        // hotAlertFilterJoins: [],
        hotAlertActionOptions: [],
        hotAlertCategories: [],
        hotAlertTakenActionOptions: [],
        hotAlertServiceQuestionNames: [],
        hotAlertAnalysisQuestionNames: [],
        hotAlertListQuestionNames: [],
        approvalListQuestionNames: [],
        virtualScores: [],
        noHotAlertTransferOnSaturday: false,
        noHotAlertTransferOnSunday: false,
        noHotAlertTransferOnHolidays: false,
        appealQuestionNames: [],
        appealPeriods: [],
        maxAppealsPerPeriod: 0,
        appealReasons: [],
        appealOnSurvey: false,
      },
      visibleSurveyQuestionNames: [],
      appealMailQuestionNames: [],
      filterQuestionNames: [],
      permissionQuestionNames: [],
      defaultWeightColumn: null,
      hotAlertListQuestionNames: [],
      hotAlertServiceQuestionNames: [],
      hotAlertAnalysisQuestionNames: [],
      approvalListQuestionNames: [],
      appealQuestionNames: [],
      appealPeriods: [],
      maxAppealsPerPeriod: 0,
      appealReasons: [],
      required,
      optionQuestion: [],
      optionQuestionNumeric: [],
    }
  },
  computed: {
    hotalertServiceQuestionOptions() {
      return this.optionQuestion.filter(x => x.questionName.indexOf('gdpr_') === -1)
    },
    hotalertListQuestionOptions() {
      return this.optionQuestion.filter(x => x.questionName.indexOf('gdpr_') === -1 && x.questionType !== 4)
    },
    approvalListQuestionOptions() {
      return this.optionQuestion.filter(x => x.questionName.indexOf('gdpr_') === -1 && x.questionType !== 4)
    },
    appealQuestionOptions() {
      return this.optionQuestion.filter(x => x.questionName.indexOf('gdpr_') === -1)
    },
    item: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        if (this.value) {
          this.$set(this, 'visibleSurveyQuestionNames', this.value.visibleSurveyQuestionNames)
          this.$set(this, 'appealMailQuestionNames', this.value.appealMailQuestionNames)
          this.$set(this, 'filterQuestionNames', this.value.filterQuestionNames)
          this.$set(this, 'permissionQuestionNames', this.value.permissionQuestionNames)
          this.$set(this, 'defaultWeightColumn', this.value.defaultWeightColumn)
          this.$set(this, 'hotAlertServiceQuestionNames', this.value.hotAlertServiceQuestionNames)
          this.$set(this, 'hotAlertListQuestionNames', this.value.hotAlertListQuestionNames)
          this.$set(this, 'hotAlertAnalysisQuestionNames', this.value.hotAlertAnalysisQuestionNames)
          this.$set(this, 'approvalListQuestionNames', this.value.approvalListQuestionNames)
          this.$set(this, 'appealQuestionNames', this.value.appealQuestionNames)
          this.$set(this, 'appealPeriods', this.value.appealPeriods)
          this.$set(this, 'maxAppealsPerPeriod', this.value.maxAppealsPerPeriod)
          this.$set(this, 'appealReasons', this.value.appealReasons)
          return this.value
        }
        return this.defaultItem
      },
    },
  },
  created() {
    this.getQuestionOptions()
  },
  methods: {
    clearAppealDate(field, pickerRef) {
      this.$refs[pickerRef].fp.clear()
      this.$set(this.item, field, null)
    },
    validationForm() {
      this.$refs.simpleRules.validate().then(success => {
        if (success) {
          const postItem = JSON.stringify(this.item)
          useJwt.axiosIns.patch(`project/${this.projectId}/settings`, { stringValue: postItem })
            .then(response => {
              if (response.data) {
                this.$emit('submit')
              }
            })
            .catch(e => {
              console.log(e)
            })
        }
      })
    },
    getQuestionOptions() {
      useJwt.axiosIns.get(`question/${this.projectId}/shortlist`).then(response => {
        this.optionQuestion = response.data
        this.optionQuestionNumeric = this.optionQuestion.filter(x => x.questionType === 3)
      })
    },
    triggerFileInput() {
      // Programmatically click the hidden file input
      this.$refs.fileInput.click()
    },

    handleFileUpload(event) {
      const file = event.target.files[0] // Get the selected file

      if (file) {
        this.uploadFile(file) // Upload the file
      }
    },

    async uploadFile(file) {
      const formData = new FormData()
      formData.append('MediaFile', file)
      formData.append('ProjectId', this.projectId)
      // formData.append('Locale', this.$i18n.locale)

      // Loading message
      await this.$swal({
        title: this.$t('Processing file...'),
        allowOutsideClick: false,
        didOpen: async () => {
          this.$swal.showLoading()

          // Upload the file to the server
          useJwt.axiosIns.post('project/UploadMedia', formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          })
            .then(response => {
              if (response.data === 'Ok') {
                this.$swal({
                  icon: 'success',
                  title: this.$t('Success!'),
                  text: this.$t('Media file paths inserted successfuly.'),
                  customClass: {
                    confirmButton: 'btn btn-success',
                  },
                })
              } else {
                this.$swal({
                  icon: 'error',
                  title: this.$t('Error!'),
                  text: `Error: ${response.data}`,
                  customClass: {
                    confirmButton: 'btn btn-danger',
                  },
                })
              }
            })
            .catch(error => {
              // Handle error
              console.error('Error uploading file', error)
            })
        },
      })
    },
  },
}
</script>
